/**
 * The desk's closed map from the editor's checks to what the candidate screen
 * highlights (D-07, DESK-02).
 *
 * Why closed: every check the editor can write must land somewhere visible. A
 * check with no target would be silently hidden from Dominique, so
 * `CHECK_TARGETS` is a `Record<DeskCheckName, CheckTarget>` (a missing name fails
 * to compile) and a Jest test pins the 14 names literally (a new name fails a
 * test). A name this file does not know is never dropped either: `unknownChecks`
 * returns it so the ChecksPanel can show it as is (T-33-38).
 *
 * The vocabularies are restated literally, not imported (loose monorepo, DEC-1).
 * Twin lists that must agree:
 * - mobile/supabase/functions/_shared/editor/extraction.ts `CHECK_NAMES` (the 13
 *   editor names, in this order) and `SOURCE_FIELD_KEYS` (the 11 source keys);
 * - mobile/supabase/migrations/20240101000035_desk.sql
 *   `applications_route_checks_allowlist` (the 13 plus `restored_from_discard`,
 *   which only the desk's raw-item restore writes, D-12) and 0031
 *   `applications_source_conflicts_allowlist`;
 * - mobile/supabase/tests/desk_test.sql (33-12), which pins the SQL side.
 * - `desk_rules` (DESK_RULES_MATCH_BY below, 33.1): 0037's
 *   applications_match_by_allowlist, editorFence's DESK_ONLY_MATCH_BY,
 *   supabase/desk/recheckMatch.ts DESK_RULES_MATCH_BY and the Match panel's
 *   caption. Restated here, not imported, because recheckMatch.ts loads the
 *   Supabase client and this file has no runtime dependencies.
 *
 * `field_sources` values (0031, D-03): `none | text | image:1..5 | both:1..5`. The
 * jsonb column has no CHECK over them, so every reader here validates and drops
 * anything else.
 *
 * Pure: no Supabase, no React, no translation lookup. Keys are returned for the
 * caller to translate.
 */

import type { ApplicationRow } from 'supabase/desk/deskTypes'

/** The 14 check names, in the editor's display priority (most actionable first). */
export const DESK_CHECK_NAMES = [
  'possible_duplicate',
  'possible_update',
  'match_unchecked',
  'title_missing',
  'start_date_missing',
  'city_unresolved',
  'status_change',
  'source_conflict',
  'fact_source_unverified',
  'relative_date_unverified',
  'far_future_date',
  'place_unverified',
  'past_start_date',
  'restored_from_discard'
] as const

export type DeskCheckName = (typeof DESK_CHECK_NAMES)[number]

/**
 * The form sections the desk can highlight. Shared contract with the EventForm
 * field wrapper (33-10): the same ids, in the same spelling.
 */
export const DESK_FIELD_IDS = [
  'title',
  'subtitle',
  'description',
  'start',
  'end',
  'place',
  'price',
  'category',
  'organizer',
  'image'
] as const

export type DeskFieldId = (typeof DESK_FIELD_IDS)[number]

export type CheckTarget =
  | { kind: 'field'; field: DeskFieldId }
  | { kind: 'image_only' }
  | { kind: 'conflicts' }
  | { kind: 'match_panel' }
  | { kind: 'match_inputs' }
  | { kind: 'status_banner' }

/**
 * D-07, as a table. `image_only`, `conflicts` and `match_inputs` resolve to
 * fields per candidate.
 *
 * `match_unchecked` points at what the match step was missing, not at the match
 * panel: the panel is only drawn for a found duplicate or update
 * (MatchPanel.showsMatchPanel), so an unchecked match has nothing to show
 * there. `match_inputs` is the town when `city_unresolved` is also a check and
 * the start when `start_date_missing` is; with neither (a failed read or model
 * call), the row names no field.
 */
export const CHECK_TARGETS: Record<DeskCheckName, CheckTarget> = {
  possible_duplicate: { kind: 'match_panel' },
  possible_update: { kind: 'match_panel' },
  match_unchecked: { kind: 'match_inputs' },
  title_missing: { kind: 'field', field: 'title' },
  start_date_missing: { kind: 'field', field: 'start' },
  city_unresolved: { kind: 'field', field: 'place' },
  status_change: { kind: 'status_banner' },
  source_conflict: { kind: 'conflicts' },
  fact_source_unverified: { kind: 'image_only' },
  relative_date_unverified: { kind: 'field', field: 'start' },
  far_future_date: { kind: 'field', field: 'start' },
  place_unverified: { kind: 'field', field: 'place' },
  past_start_date: { kind: 'field', field: 'start' },
  restored_from_discard: { kind: 'field', field: 'title' }
}

/** The eleven `field_sources` / `source_conflicts` keys, in the editor's order. */
export const DESK_SOURCE_FIELD_KEYS = [
  'title',
  'start_date',
  'end_date',
  'start_time',
  'city',
  'venue',
  'organizer',
  'price',
  'category',
  'weekly_event',
  'status'
] as const

export type DeskSourceFieldKey = (typeof DESK_SOURCE_FIELD_KEYS)[number]

/**
 * Which form section each source key lives in. `weekly_event` sits with the
 * start (the recurrence is part of the date/time section). `status` has no form
 * field (cancellation is the status banner), so it maps to null: a status
 * conflict is still listed in the ChecksPanel, it just highlights no field.
 */
export const SOURCE_FIELD_TO_DESK_FIELD: Record<DeskSourceFieldKey, DeskFieldId | null> = {
  title: 'title',
  start_date: 'start',
  end_date: 'end',
  start_time: 'start',
  city: 'place',
  venue: 'place',
  organizer: 'organizer',
  price: 'price',
  category: 'category',
  weekly_event: 'start',
  status: null
}

/** The candidate columns these readers use (an applications row or a desk_queue row). */
export type CheckRow = {
  route_checks?: readonly string[] | null
  field_sources?: unknown
  source_conflicts?: readonly string[] | null
}

export type FieldFlags = Partial<Record<DeskFieldId, DeskCheckName[]>>

export type ChecksPanelRow = {
  check: DeskCheckName
  target: CheckTarget
  /** The form sections the row scrolls to / names; [] for match panel, banner or an unmapped field. */
  fields: DeskFieldId[]
  /** For a per-field source_conflict row, the conflicting source key. */
  sourceKey: DeskSourceFieldKey | null
}

const isCheckName = (value: string): value is DeskCheckName =>
  (DESK_CHECK_NAMES as readonly string[]).includes(value)

const isSourceKey = (value: string): value is DeskSourceFieldKey =>
  (DESK_SOURCE_FIELD_KEYS as readonly string[]).includes(value)

const knownChecks = (row: CheckRow): Set<DeskCheckName> =>
  new Set((row.route_checks ?? []).filter(isCheckName))

/** field_sources as a key -> value record, or {} when it is not a plain object. */
const readFieldSources = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

const unique = <T>(values: T[]): T[] => values.filter((value, index) => values.indexOf(value) === index)

const IMAGE_ONLY_SOURCE = /^image:[1-5]$/

/** The form sections whose recorded source is image-only (`image:n`), in source-key order. */
const imageOnlyFields = (row: CheckRow): DeskFieldId[] => {
  const sources = readFieldSources(row.field_sources)
  return unique(
    DESK_SOURCE_FIELD_KEYS.flatMap((key) => {
      const field = SOURCE_FIELD_TO_DESK_FIELD[key]
      const source = sources[key]
      return field && typeof source === 'string' && IMAGE_ONLY_SOURCE.test(source) ? [field] : []
    })
  )
}

/** The known source keys named in source_conflicts, in source-key order. */
const conflictKeys = (row: CheckRow): DeskSourceFieldKey[] => {
  const listed = new Set((row.source_conflicts ?? []).filter(isSourceKey))
  return DESK_SOURCE_FIELD_KEYS.filter((key) => listed.has(key))
}

/** The form sections the match step needed and did not have, in form order. */
const matchInputFields = (row: CheckRow): DeskFieldId[] => {
  const checks = knownChecks(row)
  return [
    ...(checks.has('start_date_missing') ? ['start' as const] : []),
    ...(checks.has('city_unresolved') ? ['place' as const] : [])
  ]
}

/** The fields one check highlights on this candidate. */
const fieldsFor = (check: DeskCheckName, row: CheckRow): DeskFieldId[] => {
  const target = CHECK_TARGETS[check]
  if (target.kind === 'field') return [target.field]
  if (target.kind === 'match_inputs') return matchInputFields(row)
  if (target.kind === 'image_only') return imageOnlyFields(row)
  if (target.kind === 'conflicts') {
    return unique(conflictKeys(row).flatMap((key) => {
      const field = SOURCE_FIELD_TO_DESK_FIELD[key]
      return field ? [field] : []
    }))
  }
  return []
}

/**
 * The checks to show on each form section, each list in DESK_CHECK_NAMES order.
 * A conflict is flagged only when `source_conflict` is a check (0031 ties the two).
 */
export const flagsByField = (row: CheckRow): FieldFlags => {
  const checks = knownChecks(row)
  const flags: FieldFlags = {}
  for (const check of DESK_CHECK_NAMES) {
    if (!checks.has(check)) continue
    for (const field of fieldsFor(check, row)) {
      flags[field] = [...(flags[field] ?? []), check]
    }
  }
  return flags
}

/** The columns that say a row is an elapsed auto-reject restored by the desk. */
export type RestoreRow = CheckRow & {
  route?: string | null
  desk_restored_at?: string | null
  processed?: boolean | null
}

/**
 * D-12 case 2: a candidate the editor auto-rejected because it had ended
 * (route `auto_rejected`) and Dominique restored (`desk_restored_at`) carries
 * no check, yet it lands in Needs a look "with the start date highlighted"
 * (UI-SPEC §Restore). Its start is in the past by definition, so the row is
 * read as also carrying `past_start_date`, which already targets the start
 * field. Display only: nothing is written. Any other row is returned as is.
 */
export const withRestoreChecks = <T extends RestoreRow>(row: T): T => {
  const elapsedRestore = row.route === 'auto_rejected' && !!row.desk_restored_at && !row.processed
  const checks = row.route_checks ?? []
  if (!elapsedRestore || checks.includes('past_start_date')) return row
  return { ...row, route_checks: [...checks, 'past_start_date'] }
}

/** route_checks values this file does not know, in stored order (shown as is, never hidden). */
export const unknownChecks = (row: Pick<CheckRow, 'route_checks'>): string[] =>
  (row.route_checks ?? []).filter((value) => !isCheckName(value))

/**
 * The "What the editor flagged" rows: known route_checks in DESK_CHECK_NAMES
 * order, then one source_conflict row per conflicting field. `source_conflict`
 * itself becomes those per-field rows; it stays a single generic row only when
 * no known field is named.
 */
export const checksInOrder = (row: CheckRow): ChecksPanelRow[] => {
  const checks = knownChecks(row)
  const conflicts = checks.has('source_conflict') ? conflictKeys(row) : []

  const ordered: ChecksPanelRow[] = DESK_CHECK_NAMES
    .filter((check) => checks.has(check))
    .filter((check) => check !== 'source_conflict' || conflicts.length === 0)
    .map((check) => ({
      check,
      target: CHECK_TARGETS[check],
      fields: check === 'source_conflict' ? [] : fieldsFor(check, row),
      sourceKey: null
    }))

  const perField: ChecksPanelRow[] = conflicts.map((key) => {
    const field = SOURCE_FIELD_TO_DESK_FIELD[key]
    return {
      check: 'source_conflict',
      target: CHECK_TARGETS.source_conflict,
      fields: field ? [field] : [],
      sourceKey: key
    }
  })

  return [...ordered, ...perField]
}

/**
 * The translation key for a "What the editor flagged" row. `match_unchecked`
 * says why the match could not run when the reason is a missing town or start
 * date, so fixing that field reads as the way to the duplicate check.
 */
export const checkLabelKey = (row: ChecksPanelRow): string => {
  if (row.check !== 'match_unchecked') return `desk.checks.${row.check}`
  const noTown = row.fields.includes('place')
  const noDate = row.fields.includes('start')
  if (noTown && noDate) return 'desk.checks.match_unchecked_no_town_or_date'
  if (noTown) return 'desk.checks.match_unchecked_no_town'
  if (noDate) return 'desk.checks.match_unchecked_no_date'
  return 'desk.checks.match_unchecked'
}

/** Twin of recheckMatch.ts DESK_RULES_MATCH_BY (see the header). */
const DESK_RULES_MATCH_BY = 'desk_rules'

/** The columns withRecheckChecks reads (an applications row or a desk_queue row). */
export type RecheckChecksRow = CheckRow & {
  match_label?: string | null
  match_by?: string | null
}

const hasDeskResult = (row: RecheckChecksRow): boolean =>
  row.match_by === DESK_RULES_MATCH_BY && row.match_label !== 'unchecked'

/**
 * 33.1 D-18: the desk's re-check keeps `match_unchecked` on a row whose clean
 * result would otherwise leave Needs a look with no check, so on a row the
 * desk relabelled (`match_by = 'desk_rules'`) the label decides, not the
 * check. A desk_rules row with a result (new / possible_*) is read without
 * `match_unchecked`; any other row is returned as is (same object). Display
 * only: nothing is written.
 */
export const withRecheckChecks = <T extends RecheckChecksRow>(row: T): T => {
  const checks = row.route_checks ?? []
  if (!hasDeskResult(row) || !checks.includes('match_unchecked')) return row
  return { ...row, route_checks: checks.filter((check) => check !== 'match_unchecked') }
}

/** The columns recheckLine reads: the label plus what the last clean re-check checked. */
export type RecheckRow = RecheckChecksRow &
  Partial<Pick<ApplicationRow, 'desk_rematched_city_id' | 'desk_rematched_start_date'>>

/** What the duplicate re-check says on the flag row (keys and row values only). */
export type RecheckLine =
  | { kind: 'checking' }
  | { kind: 'clear'; cityId: number | null; startDate: string | null }
  | { kind: 'failed'; labelKey: string; retryable: boolean }

/** The re-check's state as the hook reports it (useDeskRecheck's pending / failure). */
export type RecheckState = {
  pending: boolean
  failure: { reason: string; retryable: boolean } | null
}

const failedLabelKey = (reason: string): string => {
  if (reason === 'no_town') return 'desk.checks.match_unchecked_no_town'
  if (reason === 'no_start_date') return 'desk.checks.match_unchecked_no_date'
  return 'desk.checks.match_unchecked'
}

/**
 * The re-check line for the match_unchecked slot (33.1 D-13, D-14): a check
 * in flight reads as checking; a failure keeps the "not checked" label with
 * its reason; with neither, a desk_rules row labelled new is clear, drawn
 * from the row (so it survives a reload). Anything else has nothing to say.
 */
export const recheckLine = (row: RecheckRow, state: RecheckState): RecheckLine | null => {
  if (state.pending) return { kind: 'checking' }
  if (state.failure) {
    return { kind: 'failed', labelKey: failedLabelKey(state.failure.reason), retryable: state.failure.retryable }
  }
  if (row.match_by === DESK_RULES_MATCH_BY && row.match_label === 'new') {
    return {
      kind: 'clear',
      cityId: row.desk_rematched_city_id ?? null,
      startDate: row.desk_rematched_start_date ?? null
    }
  }
  return null
}
